const PENDING_WISHLIST_ACTION_KEY = 'pendingWishlistAction'

export const PENDING_WISHLIST_ACTION = Object.freeze({
    ADD: 'add',
    MOVE_FROM_CART: 'move_from_cart'
})

/**
 * Persists any guest wishlist action so the appropriate surface can replay it after login.
 * @param {Object} action - Pending wishlist action.
 * @param {string} [action.type] - Wishlist action type.
 * @param {string} action.productId - Product ID requested by the guest.
 * @param {string|null} [action.interactionLocation] - Original GTM interaction location.
 * @param {string|null} [action.itemId] - Basket line-item ID for cart moves.
 * @param {string|null} [action.categoryId] - Product category used by Braze.
 * @returns {void}
 */
export const persistPendingWishlist = ({
    type = PENDING_WISHLIST_ACTION.ADD,
    productId,
    interactionLocation = null,
    itemId = null,
    categoryId = null
}) => {
    if (typeof window === 'undefined' || !productId) return

    window.sessionStorage.setItem(
        PENDING_WISHLIST_ACTION_KEY,
        JSON.stringify({type, productId: String(productId), interactionLocation, itemId, categoryId})
    )
}

/**
 * Gets the pending guest wishlist action.
 * @returns {{type: string, productId: string, interactionLocation: string|null, itemId: string|null, categoryId: string|null}|null}
 */
export const getPendingWishlist = () => {
    if (typeof window === 'undefined') return null

    try {
        const action = JSON.parse(window.sessionStorage.getItem(PENDING_WISHLIST_ACTION_KEY))
        return action?.productId ? action : null
    } catch {
        return null
    }
}

/**
 * Clears the pending guest wishlist action after it is handled.
 * @returns {void}
 */
export const clearPendingWishlist = () => {
    if (typeof window === 'undefined') return

    window.sessionStorage.removeItem(PENDING_WISHLIST_ACTION_KEY)
}

export const getWishlistId = async ({
    wishlist,
    customerId,
    refetchWishlist,
    createCustomerProductList
}) => {
    if (wishlist?.id) return wishlist.id
    if (!customerId) return null

    try {
        const result = await refetchWishlist?.()
        const lists = result?.data?.data || []
        const existing = lists.find((list) => list.type === 'wish_list')
        if (existing?.id) return existing.id
    } catch (error) {
        console.warn('Unable to refetch wishlist lists', error)
    }

    try {
        const created = await createCustomerProductList({
            parameters: {customerId},
            body: {type: 'wish_list'}
        })
        return created?.id || created?.data?.id || null
    } catch (error) {
        console.warn('Unable to create wishlist', error)
        return null
    }
}

// Detects the backend hook signal returned by customerProductListItemsHooks.beforePOST
// when the wishlist limit (set via the `wishlistMaxItemsCount` site preference) is reached.
export const isWishlistMaxItemsReachedError = async (error) => {
    try {
        const response = error?.response
        if (!response) return false
        // Clone before reading so the original body stream stays consumable for any other handler.
        const cloned = typeof response.clone === 'function' ? response.clone() : response
        const body = await cloned.text()
        return body.includes('MAX_ITEMS_REACHED')
    } catch {
        return false
    }
}
